import { useEffect, useRef, useState } from "react"
import { TbChartBar } from "react-icons/tb"
import { BaseTable, Modal, MultipleChart, MultipleChartData, NumberInput, TableHeader } from "components"
import { convertNumber, useToggle } from "utils"
import { ChartOptions } from "chart.js"
import { SalesMonthlyGroup, SalesMonthlyItem, MONTH_KEYS } from "../types/SalesMonthlyType"
import { useSaveFinanceBudget } from "../../Finance/utils/UseSaveFinanceBudget"

type Props = {
  list: SalesMonthlyGroup[]
  setList: React.Dispatch<React.SetStateAction<SalesMonthlyGroup[]>>
  year?: number
  isFilterReady: boolean
}

export default function SalesMonthlyTable({ list, setList, year, isFilterReady }: Props) {
  const { saveBudget } = useSaveFinanceBudget()
  const [budgets, setBudgets] = useState<Record<string, string>>({})
  const debounceTimer = useRef<Record<string, any>>({})

  useEffect(() => {
    const initial: Record<string, string> = {}
    list.forEach((group) => {
      group.items.forEach((item) => {
        const key = `${group.code_budget}_${item.location_id != null ? String(item.location_id) : item.location_name}`
        initial[key] = item.budget ?? ""
      })
    })
    setBudgets(initial)
  }, [list])

  return (
    <div className="overflow-x-auto">
      <div className="max-h-[600px] overflow-y-auto">
        <BaseTable className="table table-bordered w-full">
          <TableHeader>
            <tr className="bg-black text-white text-xs">
              <th className="text-left">KEY DESCRIPTION</th>
              <th className="text-right">BUDGET</th>
              <th className="text-right">AVERAGE</th>
              {MONTH_KEYS.map((month) => (
                <th key={month} className="text-center min-w-[80px]">
                  {month.toUpperCase()}
                </th>
              ))}
              <th className="text-center">ACTION</th>
            </tr>
          </TableHeader>
          <tbody>
            {list.length === 0 ? (
              <tr>
                <td colSpan={16} className="text-center">No data</td>
              </tr>
            ) : (
              list.map((group) => (
                <>
                  {/* Category row */}
                  <tr key={`cat-${group.code_budget}`} className="bg-base-300 font-semibold">
                    <td colSpan={16}>{group.category}</td>
                  </tr>

                  {group.items.map((item) => {
                    const rowKey = `${group.code_budget}_${item.location_id != null ? String(item.location_id) : item.location_name}`
                    return (
                      <tr key={rowKey}>
                        <td className="font-medium !align-middle ps-10 min-w-[200px]">
                          {item.location_name}
                        </td>

                        {/* Budget */}
                        <td className="text-right">
                          <NumberInput
                            className="min-w-[100px]"
                            disabled={!isFilterReady}
                            textRight
                            value={budgets[rowKey] ?? ""}
                            onValueChange={(e, sourceInfo) => {
                              if (sourceInfo.source !== "event") return
                              const value = e.floatValue?.toString() ?? ""

                              setBudgets(prev => ({ ...prev, [rowKey]: value }))

                              if (debounceTimer.current[rowKey]) clearTimeout(debounceTimer.current[rowKey])

                              debounceTimer.current[rowKey] = setTimeout(async () => {
                                if (!year || !value) return

                                const result = await saveBudget({
                                  budget_id: item.budget_id,
                                  code_budget: item.code_budget ?? group.code_budget,
                                  year,
                                  budget: value,
                                  ...(item.location_id != null ? { location_id: item.location_id } : {})
                                })

                                if (!result) return

                                const newBudgetId = result.cr_budget_loc_id ?? result.cr_budget_global_id

                                setList(prev =>
                                  prev.map(g =>
                                    g.code_budget === group.code_budget
                                      ? {
                                          ...g,
                                          items: g.items.map(row =>
                                            (row.location_id != null ? row.location_id === item.location_id : row.location_name === item.location_name)
                                              ? { ...row, budget_id: newBudgetId ?? row.budget_id, budget: value }
                                              : row
                                          )
                                        }
                                      : g
                                  )
                                )
                              }, 2000)
                            }}
                          />
                        </td>

                        {/* Average */}
                        <td className="text-right !align-middle">
                          {item.average ? convertNumber(Number(item.average)).intoCurrency : ""}
                        </td>

                        {/* Months */}
                        {MONTH_KEYS.map((month) => (
                          <td key={month} className="align-middle">
                            <div className="flex items-center justify-end h-10">
                              {item[month] ? convertNumber(Number(item[month])).intoCurrency : ""}
                            </div>
                          </td>
                        ))}

                        {/* Action */}
                        <td className="text-center !align-middle">
                          <ChartSection
                            disabled={!isFilterReady}
                            title={`${group.category} — ${item.location_name}`}
                            item={item}
                            budget={budgets[rowKey] ?? ""}
                          />
                        </td>
                      </tr>
                    )
                  })}
                </>
              ))
            )}
          </tbody>
        </BaseTable>
      </div>
    </div>
  )
}

function ChartSection({ title, item, budget, disabled }: {
  title: string
  item: SalesMonthlyItem
  budget: string
  disabled?: boolean
}) {
  const { isActive, toggle } = useToggle(false)
  const [chartType, setChartType] = useState<"line" | "bar">("line")

  const labels = MONTH_KEYS.map(m => m.toUpperCase())
  const actualValues = MONTH_KEYS.map(m => item[m] != null ? Number(item[m]) : null)
  const budgetVal = budget ? Number(budget) : null
  const averageVal = item.average ? Number(item.average) : null

  const data: MultipleChartData = {
    labels,
    datasets: [
      {
        label: "Actual",
        data: actualValues,
        borderColor: "#3B82F6",
        backgroundColor: "rgba(59,130,246,0.2)",
        borderWidth: 2,
        fill: false,
        spanGaps: false
      }
    ]
  }

  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: 20 },
    scales: {
      x: {
        position: chartType === "line" ? "bottom" : "center",
        ticks: { display: chartType === "line" },
        grid: { display: false },
        border: { display: false }
      },
      y: {
        grid: {
          color: (ctx) => ctx.tick.value === 0 ? "rgb(0,0,0)" : "rgba(0,0,0,0)"
        },
        border: { display: false }
      }
    },
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      annotation: {
        annotations: {
          ...(budgetVal != null ? {
            budgetLine: {
              type: "line" as const,
              yMin: budgetVal,
              yMax: budgetVal,
              borderColor: "#DC2626",
              borderWidth: 2,
              borderDash: [6, 6],
              label: {
                display: true,
                content: `BUDGET (${convertNumber(budgetVal).intoCurrency})`,
                position: "end" as const,
                color: "#DC2626",
                font: { weight: "bold" as const },
                yAdjust: -10,
                backgroundColor: "transparent"
              }
            }
          } : {}),
          ...(averageVal != null ? {
            averageLine: {
              type: "line" as const,
              yMin: averageVal,
              yMax: averageVal,
              borderColor: "#ff9d00",
              borderWidth: 2,
              borderDash: [6, 6],
              label: {
                display: true,
                content: `AVERAGE (${convertNumber(averageVal).intoCurrency})`,
                position: "end" as const,
                color: "#ff9d00",
                font: { weight: "bold" as const },
                yAdjust: -10,
                backgroundColor: "transparent"
              }
            }
          } : {})
        }
      }
    }
  }

  return (
    <>
      <button
        onClick={toggle}
        disabled={disabled}
        className="btn btn-xs btn-warning text-white"
      >
        <TbChartBar size={16} />
      </button>

      {isActive && (
        <Modal isOpen title={`${title.toUpperCase()} CHART`} closeModal={toggle} isdarktitle>
          <Modal.Body>
            <div className="flex justify-end mb-2">
              <select
                className="select select-sm select-bordered"
                value={chartType}
                onChange={(e) => setChartType(e.target.value as "line" | "bar")}
              >
                <option value="line">Line</option>
                <option value="bar">Bar</option>
              </select>
            </div>
            <div style={{ height: 400 }}>
              <MultipleChart
                className="min-h-[400px]"
                chartType={chartType as "bar"}
                data={data}
                options={options}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              <div className="border-2 border-gray-400 min-h-[300px] rounded-lg p-4">
                <h2 className="font-bold mb-3">FORMULA</h2>
                <div className="text-sm space-y-2">
                  <div dangerouslySetInnerHTML={{ __html: item.formula ?? "" }} />
                </div>
              </div>

              <div className="border-2 border-gray-400 min-h-[300px] rounded-lg p-4">
                <h2 className="font-bold mb-3">AI ANALYTIC</h2>
                <div className="text-sm text-gray-600">
                </div>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      )}
    </>
  )
}
